<template>
  <div v-if="data" class="flex grow flex-col">
    <div class="h-container flex grow flex-col">
      <div v-if="t['w.saal.toode.banner']()" v-html="t['w.saal.toode.banner']()" />

      <BasketMerge v-if="data.mergeInfo?.mergeLeftBasket" :logged-in-cart-items="data.mergeInfo.mergeLeftBasket" @merged="reloadPage" />

      <div v-else-if="items && items.length > 0" class="flex grow flex-col">
        <form class="flex grow flex-col" @submit.prevent="formSubmit">
          <div class="grow md:grid md:grid-cols-3 md:gap-5">
            <div class="my-8 md:col-span-2 md:my-10">
              <h1 class="mb-4 text-h4">
                {{ t['w.basket.title']() }}
                <span class="text-base font-normal">({{ productsInBasketLabel }})</span>
              </h1>
              <div class="mb-6 grid grid-cols-[max-content_1fr] gap-x-5 pt-6 md:mb-10 md:grid-cols-[max-content_1fr_max-content]">
                <BasketItem v-for="(item, itemIndex) in items" :key="`basket-item-${itemIndex}`" :item="item" @remove-product="removeProduct" @update-quantity="updateQuantity" />
              </div>
              <div class="mb-12 md:mb-0">
                <label class="mb-4 block text-h4" for="ostukorvMinuOstukorvSelgitus">{{ t['w.basket.order_note']() }}</label>
                <FloatingLabelTextareaInput
                  id="ostukorvMinuOstukorvSelgitus"
                  v-model="ostukorvMinuOstukorvSelgitus"
                  name="ostukorvMinuOstukorvSelgitus"
                  :placeholder="t['w.basket.order_note_placeholder']()"
                  rows="8"
                />
              </div>
            </div>
            <div class="relative before:inset-x-0 before:h-8 before:bg-background-light md:bg-background-light md:before:absolute">
              <BasketSummary
                ref="basketSummaryRef"
                v-model:promo-code="promoCode"
                class="md:sticky md:top-24"
                :items="items"
                :is-loading="isLoading"
                login-prompt
                has-bottom
                :can-edit-voucher="true"
                :promo-code-discount="data.tellForm.promoCodeDiscount"
              />
            </div>
          </div>
        </form>
      </div>
      <EmptyBasket v-else />
    </div>
    <!-- TODO: removed until we have actual support for this ; add logic to get "frequently bought together" (who-buy-x-also-buy-y) for basket items -->
    <!--    <client-only>-->
    <!--      <TiedProductsSection-->
    <!--        v-show="basketAlsoBuyItems && basketAlsoBuyItems.length"-->
    <!--        :title="t['w.basket.who_buy_x_also_buy_y_title']()"-->
    <!--        :who-buy-x-also-buy-y-products="basketAlsoBuyItems"-->
    <!--        @product-added="refreshItems"-->
    <!--      />-->
    <!--    </client-only>-->
  </div>
</template>

<script setup lang="ts">
import EmptyBasket from '~/components/order/EmptyBasket.vue'
import BasketMerge from '~/components/order/BasketMerge.vue'
import BasketItem from '~/components/order/BasketItem.vue'
import BasketSummary from '~/components/order/BasketSummary.vue'
import type { IBasketItem, IVoucher } from '~/components/order/BasketTypes'
//import { useBasketAlsoBuyState, useBasketState } from '~/composables/states'
//import TiedProductsSection from '~/components/product/TiedProductsSection.vue'
import FloatingLabelTextareaInput from '~/components/input/FloatingLabelTextareaInput.vue'
import { useWritableRef } from '~/composables/readonly'
import * as t from '~/messages'
import { useBasketQuery, useAbsoluteQuantityChangeMutation } from '~/composables/queries/basket'

const { proxy: gtmProxy } = useGtm()

const { data, error, suspense } = await useBasketQuery()
await suspense()
if (error.value) {
  throw createError(error.value)
}

// const basketAlsoBuyItems = useBasketAlsoBuyState()
//
// const refreshItems = async () => {
//   await refresh()
//   items.value = data.value.items
// }

const items = useWritableRef(data.value.items)
watch(
  () => data.value?.items ?? [],
  (newValue) => {
    items.value = newValue
  }
)

const ostukorvMinuOstukorvSelgitus = ref(data.value.tellForm.ostukorvMinuOstukorvSelgitus ?? '')
const promoCode = ref<IVoucher | null>(data.value.tellForm.promoCode)

const productsInBasketLabel = computed(() => {
  const quantity = items.value.length

  if (quantity > 1) {
    return t['w.basket.products_in_basket_plural']({ quantity })
  }

  return t['w.basket.products_in_basket']({ quantity })
})

const { mutate: changeQuantity } = useAbsoluteQuantityChangeMutation()

const removeProduct = (item: IBasketItem) => {
  items.value = items.value.filter((x) => x !== item)
  changeQuantity({
    itemId: item.productId,
    absKogus: 0,
    optionIds: item.optionIds,
    variationId: item.variationId,
  })
}

const updateQuantity = (item: IBasketItem) => {
  changeQuantity({
    itemId: item.productId,
    absKogus: item.quantity,
    optionIds: item.optionIds,
    variationId: item.variationId,
  })
}

useHead({
  title: () => data.value?.pageTitle ?? null,
  meta: [{ name: 'robots', content: 'noindex, follow' }],
})

const isLoading = ref(false)

useCurrentTransportStepState().value = 'transport'

const formSubmit = async () => {
  const formData = new URLSearchParams({
    ostukorvMinuOstukorvSelgitus: ostukorvMinuOstukorvSelgitus.value ?? '',
    kontaktisik: data.value?.tellForm.kontaktisik ?? '',
    maksja: data.value?.tellForm.maksja ?? '',
    telefon: data.value?.tellForm.telefon ?? '',
    epost: data.value?.tellForm.epost ?? '',
    tanav: data.value?.tellForm.tanav ?? '',
    postiindeks: data.value?.tellForm.postiindeks ?? '',
    linn: data.value?.tellForm.linn ?? '',
    transportviis: data.value?.tellForm.transportviis.toString() ?? '',
    automaatId: data.value?.tellForm.automaatId.toString() ?? '',
    customerWantsAssemblyQuote: data.value?.tellForm.customerWantsAssemblyQuote ? 'on' : '',
    promoCode: promoCode.value?.name ?? '',
  })

  isLoading.value = true
  try {
    await $fetch<unknown>('/api/order/basket', {
      method: 'POST',
      body: formData,
      redirect: 'manual',
    })

    isLoading.value = false
    await navigateTo('/order/transport')
  } catch {
    isLoading.value = false
  }
}

const basketSummaryRef = useTemplateRef('basketSummaryRef')

const sendGtmBasketView = () => {
  const {
    public: { languageId },
  } = useRuntimeConfig()

  const languagePrefix = languageId === 3 ? 'ru-' : ''
  let productIdList = ''
  if (items.value) {
    productIdList = items.value.map((product) => `${languagePrefix}${product.productId}`).join(',')
  }

  if (basketSummaryRef.value) {
    const readableBasketSum = (basketSummaryRef.value.priceSum / 100000).toFixed(2)

    gtmProxy.dataLayer.push({
      event: 'fireRemarketingTag',
      google_tag_params: {
        dynx_itemid: productIdList,
        dynx_pagetype: 'conversionintent',
        dynx_totalvalue: parseFloat(readableBasketSum),
      },
    })
  }
}

const reloadPage = () => window.location.reload()

onMounted(() => {
  sendGtmBasketView()
})
</script>
